import { Copy, MessageCircle, Phone } from "lucide-react";
import { useState, type ReactNode, type SyntheticEvent } from "react";

import { showToast } from "@/components/shell/toast";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetDescription,
  SheetPanel,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatPhone, phoneDigits, telHref, whatsAppHref } from "@/lib/phone";
import { cn } from "@/lib/utils";

interface PhoneNumberProps {
  /** As stored; shown as `+91 96464 07795`. */
  number: string;
  /** Who it rings, for the sheet's heading: "Call Myra Joshi?". */
  name?: string | null;
  /**
   * Runs before the dialler opens — the call log records the attempt here
   * (#636). A failure is said, and the call still goes ahead: the person
   * pressed Call, and a missing log row is less harm than a call not made.
   */
  onCall?: () => Promise<void> | void;
  /** Extra lines in the sheet, above the actions: earlier call attempts. */
  slot?: ReactNode;
  className?: string;
}

/** Keeps a press inside the number from also opening the row it sits in. */
function stop(event: SyntheticEvent) {
  event.stopPropagation();
}

/**
 * A phone number with a separate Call button (#635), used by every screen
 * that shows one.
 *
 * The number stays plain text: a fully tappable number in a list is dialled by
 * a thumb scrolling past it. Only the Call button is a target, 44 px tall on a
 * phone, and it opens a confirm step, so a call starts on the second tap. The
 * step offers Call, WhatsApp and Copy: on a phone a short bottom sheet with the
 * three in one row, on a desktop a small popover that leads with Copy, since a
 * desktop has no dialler.
 */
export function PhoneNumber({
  number,
  name,
  onCall,
  slot,
  className,
}: PhoneNumberProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const shown = formatPhone(number);

  const trigger = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={`Call ${shown}`}
      onClick={(event) => {
        stop(event);
        setOpen(true);
      }}
      className="h-7 gap-1 rounded-full px-2.5 text-xs max-md:min-h-11 max-md:px-3.5 max-md:text-sm"
    >
      <Phone aria-hidden className="size-3.5" />
      Call
    </Button>
  );

  const actions = (
    <PhoneActions
      number={number}
      shown={shown}
      name={name ?? null}
      onCall={onCall}
      slot={slot}
      leadWithCopy={!isMobile}
      onDone={() => {
        setOpen(false);
      }}
    />
  );

  return (
    <span
      className={cn("inline-flex flex-wrap items-center gap-x-2", className)}
    >
      <span className="tabular-nums">{shown}</span>
      {isMobile ? (
        <Sheet open={open} onOpenChange={setOpen}>
          {trigger}
          <SheetPanel
            side="bottom"
            className="h-auto px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={stop}
          >
            <div
              aria-hidden
              className="bg-muted-foreground/30 mx-auto mb-3 h-1 w-9 rounded-full"
            />
            {actions}
          </SheetPanel>
        </Sheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="start" className="w-80" onClick={stop}>
            {actions}
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

function PhoneActions({
  number,
  shown,
  name,
  onCall,
  slot,
  leadWithCopy,
  onDone,
}: {
  number: string;
  shown: string;
  name: string | null;
  onCall?: () => Promise<void> | void;
  slot?: ReactNode;
  leadWithCopy: boolean;
  onDone: () => void;
}) {
  const [calling, setCalling] = useState(false);

  async function call() {
    setCalling(true);
    try {
      await onCall?.();
    } catch {
      showToast({
        title: "The call was not logged",
        description: "It still goes ahead. Note it on the order if it matters.",
        tone: "error",
      });
    } finally {
      setCalling(false);
    }
    window.location.assign(telHref(number));
    onDone();
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(`+${phoneDigits(number)}`);
      showToast({ title: "Number copied", durationMs: 3000 });
    } catch {
      showToast({
        title: "Could not copy",
        description: `Select it instead: ${shown}`,
        tone: "error",
      });
    }
    onDone();
  }

  const buttons = [
    <Button
      key="call"
      type="button"
      size="sm"
      variant={leadWithCopy ? "outline" : "default"}
      disabled={calling}
      onClick={() => {
        void call();
      }}
      className="min-h-11 flex-1 gap-1.5 md:min-h-9"
    >
      <Phone aria-hidden />
      {calling ? "Calling…" : "Call"}
    </Button>,
    <Button
      key="whatsapp"
      size="sm"
      variant="outline"
      asChild
      className="min-h-11 flex-1 gap-1.5 md:min-h-9"
    >
      <a
        href={whatsAppHref(number)}
        target="_blank"
        rel="noreferrer"
        onClick={onDone}
      >
        <MessageCircle aria-hidden />
        WhatsApp
      </a>
    </Button>,
    <Button
      key="copy"
      type="button"
      size="sm"
      variant={leadWithCopy ? "default" : "outline"}
      onClick={() => {
        void copy();
      }}
      className="min-h-11 flex-1 gap-1.5 md:min-h-9"
    >
      <Copy aria-hidden />
      Copy
    </Button>,
  ];

  const Title = leadWithCopy ? "p" : SheetTitle;
  const Description = leadWithCopy ? "p" : SheetDescription;

  return (
    <div className="space-y-3">
      <div>
        <Title className="text-sm font-semibold">
          {name ? `Call ${name}?` : "Call this number?"}
        </Title>
        <Description className="text-foreground mt-0.5 text-lg font-semibold tabular-nums">
          {shown}
        </Description>
      </div>
      {slot}
      <div className="flex gap-2">
        {leadWithCopy ? [buttons[2], buttons[1], buttons[0]] : buttons}
      </div>
    </div>
  );
}
